/**
 * Tenants page. SuperAdmin sees + manages all tenants on the instance;
 * TenantAdmin sees only their own. Create/delete are SuperAdmin-only (the
 * server enforces this; we also hide the buttons).
 */

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import { LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tenantApi, type Tenant } from "@/lib/proxy-api";
import { isSuperAdmin } from "@/lib/rbac";
import { useHasInstance } from "@/lib/use-instance";
import { ApiException } from "@/lib/api-client";

export function TenantsPage() {
  const sa = isSuperAdmin();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const hasInstance = useHasInstance();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenantApi.list(),
    enabled: hasInstance,
  });

  const createMut = useMutation({
    mutationFn: () => tenantApi.create({ name, description: description || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setShowCreate(false);
      setName("");
      setDescription("");
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof ApiException ? err.message : "Failed to create tenant");
    },
    onSettled: () => setCreating(false),
  });

  const deleteMut = useMutation({
    mutationFn: (tenant: Tenant) => tenantApi.delete(tenant.id, true, tenant.name),
    onSuccess: async (_result, tenant) => {
      qc.setQueryData<{ business: Tenant[] }>(["tenants"], (current) => (
        current ? { ...current, business: current.business.filter((item) => item.id !== tenant.id) } : current
      ));
      clearTenantScopedCache(qc, tenant.id);
      if (location.pathname === `/tenants/${encodeURIComponent(tenant.id)}` || location.pathname.startsWith(`/tenants/${encodeURIComponent(tenant.id)}/`)) {
        navigate("/tenants", { replace: true });
      }
      await qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const tenants: Tenant[] = data?.business ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenants"
        description="ServisSearch tenants on this instance. Each owns one Bedrock Knowledge Base."
        actions={
          sa ? (
            <Button onClick={() => setShowCreate((v) => !v)}>
              <Plus className="h-4 w-4" />
              New tenant
            </Button>
          ) : undefined
        }
      />

      {showCreate && (
        <form
          className="rounded-lg border bg-card p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setCreating(true);
            createMut.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-name">Name</Label>
              <Input
                id="t-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-desc">Description (optional)</Label>
              <Input
                id="t-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating || !name}>
              {creating ? "Creating…" : "Create tenant"}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <LoadingRow cols={3} />
      ) : error ? (
        <ErrorState message={error instanceof ApiException ? error.message : "Failed to load tenants"} />
      ) : tenants.length === 0 ? (
        <EmptyState message="No tenants yet." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-20">Open</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tenant Key</TableHead>
                <TableHead>Knowledge Base</TableHead>
                <TableHead>Identifier</TableHead>
                {sa && <TableHead className="w-16 text-right">Delete</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => {
                const isDeleting = deleteMut.isPending && deleteMut.variables?.id === t.id;
                return (
                <TableRow key={t.id} className={isDeleting ? "opacity-60" : undefined}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      title="Open tenant workspace"
                    >
                      <Link to={`/tenants/${encodeURIComponent(t.id)}`}>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={t.status} />
                  </TableCell>
                  <TableCell>
                    <KeyStatusBadge hasKey={t.hasTenantAdminKey} />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {t.knowledge_base_id ? truncate(t.knowledge_base_id, 18) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {t.identifier ?? "—"}
                  </TableCell>
                  {sa && (
                    <TableCell className="text-right">
                      <ConfirmDangerButton
                        title="Delete tenant"
                        resourceName={t.name}
                        resourceIdentifier={t.id}
                        confirmationText={tenantDeleteConfirmation(t)}
                        actionLabel="Delete tenant"
                        description={`This permanently removes tenant "${t.name}" with id "${t.id}", including its collections, documents, and Bedrock Knowledge Base.`}
                        disabled={deleteMut.isPending}
                        onConfirm={async () => { await deleteMut.mutateAsync(t); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </ConfirmDangerButton>
                    </TableCell>
                  )}
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function tenantDeleteConfirmation(tenant: Tenant): string {
  return `${tenant.name} (${tenant.id})`;
}

function clearTenantScopedCache(qc: ReturnType<typeof useQueryClient>, tenantId: string) {
  qc.removeQueries({ queryKey: ["tenant", tenantId] });
  qc.removeQueries({ queryKey: ["collections", tenantId] });
  qc.removeQueries({ queryKey: ["collection-document-counts", tenantId] });
  qc.removeQueries({ queryKey: ["document-analytics", tenantId] });
  qc.removeQueries({ queryKey: ["documents", tenantId] });
  qc.removeQueries({ queryKey: ["api-keys", tenantId] });
  qc.removeQueries({ queryKey: ["analytics-overview", tenantId] });
  qc.removeQueries({ queryKey: ["analytics-search", tenantId] });
  qc.removeQueries({ queryKey: ["analytics-usage", tenantId] });
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge variant="outline">unknown</Badge>;
  const tone =
    status === "active"
      ? "bg-green-50 text-green-700 border-green-200"
      : status === "provisioning"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-red-50 text-red-700 border-red-200";
  return <Badge variant="outline" className={tone}>{status}</Badge>;
}

function KeyStatusBadge({ hasKey }: { hasKey?: boolean }) {
  return hasKey ? (
    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      stored
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
      <AlertTriangle className="mr-1 h-3 w-3" />
      missing
    </Badge>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
